import { z } from "zod";
import {
  commandContextSchema,
  commandError,
  stableFingerprint,
  type AuditEntry,
  type CommandContext,
  type CommandReceipt,
  type CommandResult,
  type CommandSuccess,
} from "../../lib/command-contract";
import type { ActivityEvent } from "../../lib/types";
import { contentHash } from "../communication/threading";
import { buildAiContext, type AiContextSource } from "./context-builder";
import {
  AI_OUTPUT_SCHEMA_VERSION,
  AI_OBJECTIVES,
  AI_FEEDBACK_KINDS,
  type AiBudgetPeriod,
  type AiExecutionMode,
  type AiFeedback,
  type AiPromptVersion,
  type AiRequest,
  type AiResult,
  type ApproachTypeVersion,
  type ReplyClassificationVersion,
} from "./contracts";
import { evaluateAiBudget, reconcileAiBudget, reserveAiBudget } from "./policies";
import { generateAiSuggestion } from "./pipeline";
import { normalizeAiProviderJson, type AiProviderPort } from "./provider";

/**
 * Comandos locais da assistência de IA.
 *
 * Eles são a única fronteira de escrita da feature. Nenhum comando envia,
 * enfileira ou confirma mensagem, altera `last_interaction_at` ou fala com
 * provider real: a geração usa a porta de provider recebida por dependência.
 */

export interface AiCommandState extends AiContextSource {
  aiPromptVersions: AiPromptVersion[];
  replyClassificationVersions: ReplyClassificationVersion[];
  approachTypeVersions: ApproachTypeVersion[];
  aiRequests: AiRequest[];
  aiResults: AiResult[];
  aiFeedback: AiFeedback[];
  aiBudgetPeriods: AiBudgetPeriod[];
  commandReceipts: CommandReceipt[];
  auditLog: AuditEntry[];
  events: ActivityEvent[];
}

export interface AiCommandDeps {
  now: () => string;
  uuid: () => string;
  provider: AiProviderPort;
  /** `live` chama um provider real, mas continua produzindo apenas sugestão revisável. */
  executionMode?: AiExecutionMode;
  /** Autoriza somente a franquia gratuita, sem orçamento monetário ou fallback pago. */
  freeTierOnly?: boolean;
}

export interface AiExecution<T> {
  result: CommandResult<T>;
  state: AiCommandState;
  /** Verdadeiro quando o estado mudou e deve ser gravado, mesmo em falha. */
  persist: boolean;
}

const INVALID_AI_COMMAND_CONTEXT: CommandContext = Object.freeze({
  ownerId: "invalid-context",
  actorType: "system",
  actorId: "ai-command",
  origin: "system",
  commandId: "invalid-context",
  correlationId: "invalid-context",
  idempotencyKey: "invalid-context",
  requestedAt: "1970-01-01T00:00:00.000Z",
  schemaVersion: 1,
});

function parseAiCommandContext(value: unknown): CommandContext | undefined {
  try {
    const normalized = normalizeAiProviderJson(value);
    if (normalized === undefined) return undefined;
    const parsed = commandContextSchema.safeParse(normalized);
    return parsed.success ? parsed.data : undefined;
  } catch {
    return undefined;
  }
}

function invalidAiCommandContext<T>(state: AiCommandState): AiExecution<T> {
  return {
    state,
    persist: false,
    result: commandError(INVALID_AI_COMMAND_CONTEXT, "validation_error", "Contexto do comando inválido.") as CommandResult<T>,
  };
}

function cloneState(state: AiCommandState): AiCommandState {
  return {
    ...state,
    aiPromptVersions: [...state.aiPromptVersions],
    replyClassificationVersions: [...state.replyClassificationVersions],
    approachTypeVersions: [...state.approachTypeVersions],
    aiRequests: [...state.aiRequests],
    aiResults: [...state.aiResults],
    aiFeedback: [...state.aiFeedback],
    aiBudgetPeriods: [...state.aiBudgetPeriods],
    commandReceipts: [...state.commandReceipts],
    auditLog: [...state.auditLog],
    events: [...state.events],
  };
}

function failure<T>(
  state: AiCommandState,
  context: CommandContext,
  code: Parameters<typeof commandError>[1],
  message: string,
  options: { retryable?: boolean; persist?: boolean; nextState?: AiCommandState } = {},
): AiExecution<T> {
  return {
    state: options.nextState ?? state,
    persist: options.persist ?? false,
    result: commandError(context, code, message, {
      retryable: options.retryable ?? false,
      severity: code === "persistence_error" ? "error" : "warning",
    }) as CommandResult<T>,
  };
}

function replayLookup<T>(
  state: AiCommandState,
  context: CommandContext,
  command: string,
  fingerprint: string,
): AiExecution<T> | null {
  const receipt = state.commandReceipts.find(
    (item) =>
      item.ownerId === context.ownerId &&
      item.command === command &&
      item.idempotencyKey === context.idempotencyKey,
  );
  if (!receipt) return null;
  if (receipt.requestFingerprint !== fingerprint)
    return failure<T>(
      state,
      context,
      "idempotency_conflict",
      "A chave de idempotência já foi usada para outra intenção.",
    );
  if (!receipt.result.ok) return { state, persist: false, result: receipt.result as CommandResult<T> };
  return {
    state,
    persist: false,
    result: { ...receipt.result, status: "replayed" } as CommandSuccess<T>,
  };
}

interface CommitInput<T> {
  state: AiCommandState;
  context: CommandContext;
  command: string;
  fingerprint: string;
  targetType: string;
  targetId: string;
  value: T;
  version: number;
  events?: ActivityEvent[];
  warnings?: string[];
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  deps: AiCommandDeps;
}

function commit<T>(input: CommitInput<T>): AiExecution<T> {
  const occurredAt = input.deps.now();
  const auditId = input.deps.uuid();
  const result: CommandSuccess<T> = {
    ok: true,
    status: "applied",
    value: input.value,
    commandId: input.context.commandId,
    correlationId: input.context.correlationId,
    version: input.version,
    eventIds: (input.events ?? []).map((event) => event.id),
    outboxJobIds: [],
    auditId,
    warnings: input.warnings ?? [],
  };
  const audit: AuditEntry = {
    id: auditId,
    ownerId: input.context.ownerId,
    command: input.command,
    commandId: input.context.commandId,
    correlationId: input.context.correlationId,
    causationId: input.context.causationId,
    actorType: input.context.actorType,
    actorId: input.context.actorId,
    origin: input.context.origin,
    targetType: input.targetType,
    targetId: input.targetId,
    idempotencyKey: input.context.idempotencyKey,
    requestFingerprint: input.fingerprint,
    result: "applied",
    occurredAt,
    expectedVersion: input.context.expectedVersion,
    nextVersion: input.version,
    before: input.before,
    after: input.after,
  };
  const receipt: CommandReceipt = {
    id: input.deps.uuid(),
    ownerId: input.context.ownerId,
    command: input.command,
    idempotencyKey: input.context.idempotencyKey,
    requestFingerprint: input.fingerprint,
    commandId: input.context.commandId,
    correlationId: input.context.correlationId,
    result,
    createdAt: occurredAt,
    schemaVersion: input.context.schemaVersion,
  };
  return {
    result,
    persist: true,
    state: {
      ...input.state,
      events: [...input.state.events, ...(input.events ?? [])],
      auditLog: [...input.state.auditLog, audit],
      commandReceipts: [...input.state.commandReceipts, receipt],
    },
  };
}

function persistFailure<T>(
  state: AiCommandState,
  context: CommandContext,
  command: string,
  fingerprint: string,
  request: AiRequest,
  code: Parameters<typeof commandError>[1],
  message: string,
  retryable: boolean,
  deps: AiCommandDeps,
): AiExecution<T> {
  const auditId = deps.uuid();
  const result = {
    ...commandError(context, code, message, { retryable }),
    auditId,
  } as CommandResult<T>;
  const audit: AuditEntry = {
    id: auditId,
    ownerId: context.ownerId,
    command,
    commandId: context.commandId,
    correlationId: context.correlationId,
    actorType: context.actorType,
    actorId: context.actorId,
    origin: context.origin,
    targetType: "ai_request",
    targetId: request.id,
    idempotencyKey: context.idempotencyKey,
    requestFingerprint: fingerprint,
    result: "failed",
    occurredAt: deps.now(),
    errorCode: request.errorCode,
    after: { status: request.status },
  };
  const receipt: CommandReceipt = {
    id: deps.uuid(),
    ownerId: context.ownerId,
    command,
    idempotencyKey: context.idempotencyKey,
    requestFingerprint: fingerprint,
    commandId: context.commandId,
    correlationId: context.correlationId,
    result,
    createdAt: deps.now(),
    schemaVersion: context.schemaVersion,
  };
  return {
    result,
    persist: true,
    state: {
      ...state,
      aiRequests: [...state.aiRequests, request],
      auditLog: [...state.auditLog, audit],
      commandReceipts: [...state.commandReceipts, receipt],
    },
  };
}

// ---------------------------------------------------------------------------
// Sugestão simulada
// ---------------------------------------------------------------------------

export const requestAiSuggestionSchema = z.object({
  threadId: z.string().trim().min(1),
  messageId: z.string().trim().min(1),
  objective: z.enum(AI_OBJECTIVES).default("compose_reply"),
  promptVersionId: z.string().trim().min(1).optional(),
  regeneratedFromRequestId: z.string().trim().min(1).optional(),
});

export const AI_REGENERATION_CONTEXT_MISMATCH_MESSAGE =
  "Regeneração exige o mesmo pedido, mensagem, contexto e versão de prompt do resultado anterior.";

export type RequestAiSuggestionInput = z.input<typeof requestAiSuggestionSchema>;

export interface RequestAiSuggestionValue {
  request: AiRequest;
  result: AiResult;
  /** Resultado imediatamente anterior da cadeia; permanece auditável. */
  previousResultId?: string;
}

const ZERO_COST_ESTIMATE_CENTS = 0;

export async function executeRequestAiSuggestion(
  stateInput: AiCommandState,
  input: RequestAiSuggestionInput,
  contextInput: CommandContext,
  deps: AiCommandDeps,
): Promise<AiExecution<RequestAiSuggestionValue>> {
  const context = parseAiCommandContext(contextInput);
  if (!context) return invalidAiCommandContext<RequestAiSuggestionValue>(stateInput);
  const command = "ai.suggestion.request";
  const executionMode = deps.executionMode ?? "simulated";

  let parsed: z.output<typeof requestAiSuggestionSchema>;
  try {
    parsed = requestAiSuggestionSchema.parse(normalizeAiProviderJson(input));
  } catch (error) {
    const message =
      error instanceof z.ZodError ? error.issues[0]?.message ?? "Pedido inválido." : "Pedido inválido.";
    return failure(stateInput, context, "validation_error", message);
  }

  const fingerprint = stableFingerprint({ ...parsed, mode: executionMode });
  const state = cloneState(stateInput);
  const replay = replayLookup<RequestAiSuggestionValue>(state, context, command, fingerprint);
  if (replay) return replay;

  const promptVersion = parsed.promptVersionId
    ? state.aiPromptVersions.find(
        (item) =>
          item.id === parsed.promptVersionId &&
          item.ownerId === context.ownerId &&
          item.objective === parsed.objective &&
          item.status === "published",
      )
    : state.aiPromptVersions
        .filter(
          (item) =>
            item.ownerId === context.ownerId &&
            item.objective === parsed.objective &&
            item.status === "published",
        )
        .sort((left, right) => right.version - left.version || right.createdAt.localeCompare(left.createdAt))[0];
  if (!promptVersion)
    return failure(stateInput, context, "not_found", "Nenhuma versão publicada de prompt para este objetivo.");

  const classificationVersion = state.replyClassificationVersions.find(
    (item) => item.id === promptVersion.replyClassificationVersionId && item.ownerId === context.ownerId,
  );
  const approachVersion = state.approachTypeVersions.find(
    (item) => item.id === promptVersion.approachTypeVersionId && item.ownerId === context.ownerId,
  );
  if (!classificationVersion || !approachVersion)
    return failure(stateInput, context, "not_found", "Catálogos versionados do prompt não encontrados.");

  const now = deps.now();
  const budgetPeriod = state.aiBudgetPeriods.find(
    (item) => item.ownerId === context.ownerId && item.periodStart <= now && now <= item.periodEnd,
  );
  const budget = executionMode === "live" && deps.freeTierOnly
    ? {
        state: "ok" as const,
        allowed: true,
        usedRatio: null,
        warnings: ["Provider local restrito à franquia gratuita; nenhum fallback pago está autorizado."],
      }
    : evaluateAiBudget(budgetPeriod, executionMode);
  if (!budget.allowed)
    return failure(
      stateInput,
      context,
      "invalid_state",
      budget.blockReason === "budget_exhausted"
        ? "Orçamento mensal de IA esgotado: novas operações dependentes de IA estão pausadas."
        : "A IA automática exige um orçamento mensal configurado.",
    );

  // Reserva e reconciliação são contratos puros nesta etapa: o modo simulado
  // não gera custo e o preço real permanece pendente de decisão.
  const reservedPeriod = budgetPeriod
    ? reserveAiBudget(budgetPeriod, ZERO_COST_ESTIMATE_CENTS, now)
    : undefined;

  const build = buildAiContext(state, {
    ownerId: context.ownerId,
    objective: parsed.objective,
    threadId: parsed.threadId,
    messageId: parsed.messageId,
    promptVersion,
    classificationVersion,
    approachVersion,
  });
  if (!build.ok)
    return failure(
      stateInput,
      context,
      ["thread_not_found", "message_not_found", "lead_not_found"].includes(build.code)
        ? "not_found"
        : "invalid_state",
      build.message,
    );

  const contextValue = build.context;
  const requestThread = state.threads.find(
    (item) => item.id === parsed.threadId && item.ownerId === context.ownerId,
  );
  if (!requestThread)
    return failure(stateInput, context, "not_found", "Conversa do pedido de IA não encontrada.");
  const previousRequest = parsed.regeneratedFromRequestId
    ? state.aiRequests.find(
        (item) => item.id === parsed.regeneratedFromRequestId && item.ownerId === context.ownerId,
      )
    : undefined;
  const previousResult = previousRequest
    ? state.aiResults.find(
        (item) => item.requestId === previousRequest.id && item.ownerId === context.ownerId,
      )
    : undefined;
  if (parsed.regeneratedFromRequestId && (!previousRequest || !previousResult))
    return failure(stateInput, context, "not_found", "Resultado anterior da regeneração não encontrado.");
  if (
    previousRequest &&
    (previousRequest.status !== "succeeded" ||
      previousRequest.threadId !== parsed.threadId ||
      previousRequest.messageId !== parsed.messageId ||
      previousRequest.objective !== parsed.objective ||
      previousRequest.promptVersionId !== promptVersion.id ||
      previousRequest.contextHash !== contextValue.hash)
  )
    return failure(
      stateInput,
      context,
      "invalid_state",
      AI_REGENERATION_CONTEXT_MISMATCH_MESSAGE,
    );

  const requestId = deps.uuid();
  const baseRequest: AiRequest = {
    id: requestId,
    ownerId: context.ownerId,
    objective: parsed.objective,
    mode: executionMode,
    leadId: contextValue.facts.lead.ref.id,
    companyId: contextValue.facts.company!.ref.id,
    contactId: contextValue.facts.contact?.ref.id,
    funnelId: contextValue.facts.funnel!.ref.id,
    threadId: parsed.threadId,
    messageId: parsed.messageId,
    mailboxId: requestThread.mailboxId,
    contextRefs: contextValue.refs,
    contextHash: contextValue.hash,
    contextTruncated: contextValue.truncated,
    omittedRefs: contextValue.omittedRefs,
    promptVersionId: promptVersion.id,
    promptVersion: promptVersion.version,
    model: promptVersion.model,
    reasoningEffort: promptVersion.reasoningEffort,
    maxOutputTokens: promptVersion.maxOutputTokens,
    outputSchemaVersion: AI_OUTPUT_SCHEMA_VERSION,
    status: "pending",
    idempotencyKey: context.idempotencyKey,
    requestFingerprint: fingerprint,
    commandId: context.commandId,
    correlationId: context.correlationId,
    regeneratedFromRequestId: parsed.regeneratedFromRequestId,
    createdAt: now,
  };

  const generation = await generateAiSuggestion({
    requestId,
    mode: executionMode,
    context: contextValue,
    promptVersion,
    provider: deps.provider,
    selectedMessageId: parsed.messageId,
    threadId: parsed.threadId,
    leadId: baseRequest.leadId,
    now,
  });

  if (generation.status !== "succeeded") {
    const completedAt = deps.now();
    const status =
      generation.status === "refused"
        ? "refused"
        : generation.status === "invalid_output"
          ? "invalid_output"
          : "failed";
    const errorCode =
      generation.status === "failed"
        ? generation.code
        : generation.status === "stale"
          ? "ambiguous_external_result"
          : "provider_permanent";
    const failedRequest: AiRequest = { ...baseRequest, status, errorCode, completedAt };
    const message =
      generation.status === "refused"
        ? "O modelo recusou produzir esta resposta. Uma pessoa precisa assumir a conversa."
        : generation.status === "invalid_output"
          ? "A saída não respeitou o schema estrito e foi descartada por inteiro."
          : generation.status === "stale"
            ? "A resposta chegou fora de contexto e foi descartada. Gere novamente."
            : generation.message;
    return persistFailure(
      state,
      context,
      command,
      fingerprint,
      failedRequest,
      errorCode,
      message,
      generation.status === "failed" ? generation.retryable : generation.status === "stale",
      deps,
    );
  }

  const completedAt = deps.now();
  const request: AiRequest = { ...baseRequest, status: "succeeded", completedAt };
  const result: AiResult = {
    id: deps.uuid(),
    ownerId: context.ownerId,
    requestId,
    outputSchemaVersion: AI_OUTPUT_SCHEMA_VERSION,
    output: generation.output,
    usage: generation.usage,
    latencyMs: generation.latencyMs,
    warnings: [...budget.warnings, ...generation.warnings],
    handoff: generation.handoff,
    promptVersionId: promptVersion.id,
    promptVersion: promptVersion.version,
    model: promptVersion.model,
    mode: executionMode,
    supersedesResultId: previousResult?.id,
    supersedesRequestId: previousRequest?.id,
    createdAt: completedAt,
  };

  const membershipId = state.memberships.find(
    (item) =>
      item.leadId === request.leadId && (!request.funnelId || item.funnelId === request.funnelId),
  )?.id;

  // Evento auditável de IA: NUNCA conta como interação real com o lead.
  const event: ActivityEvent = {
    id: deps.uuid(),
    leadId: request.leadId,
    membershipId,
    type: "ai_suggestion_generated",
    title: executionMode === "live" ? "Sugestão de IA gerada (Groq local)" : "Sugestão de IA gerada (simulada)",
    description: `Classificação ${generation.output.classification} · confiança ${Math.round(
      generation.output.confidence * 100,
    )}%`,
    occurredAt: completedAt,
    kind: "event",
    actorType: context.actorType,
    actorId: context.actorId,
    origin: context.origin,
    commandId: context.commandId,
    correlationId: context.correlationId,
    countsAsInteraction: false,
  };

  const reconciledPeriod = reservedPeriod
    ? reconcileAiBudget(reservedPeriod, ZERO_COST_ESTIMATE_CENTS, ZERO_COST_ESTIMATE_CENTS, completedAt)
    : undefined;

  const nextState: AiCommandState = {
    ...state,
    aiRequests: [...state.aiRequests, request],
    aiResults: [...state.aiResults, result],
    aiBudgetPeriods: reconciledPeriod
      ? state.aiBudgetPeriods.map((item) => (item.id === reconciledPeriod.id ? reconciledPeriod : item))
      : state.aiBudgetPeriods,
  };

  return commit<RequestAiSuggestionValue>({
    state: nextState,
    context,
    command,
    fingerprint,
    targetType: "ai_request",
    targetId: request.id,
    value: { request, result, previousResultId: previousResult?.id },
    version: 1,
    events: [event],
    warnings: result.warnings,
    after: { status: request.status, classification: generation.output.classification },
    deps,
  });
}

// ---------------------------------------------------------------------------
// Feedback humano
// ---------------------------------------------------------------------------

export const recordAiFeedbackSchema = z
  .object({
    requestId: z.string().trim().min(1),
    resultId: z.string().trim().min(1),
    kind: z.enum(AI_FEEDBACK_KINDS),
    /** Texto humano; apenas o hash é guardado, o corpo vive no rascunho. */
    editedText: z.string().trim().min(1).max(200_000).optional(),
    draftMessageId: z.string().trim().min(1).optional(),
    note: z.string().trim().max(1_000).optional(),
  })
  .superRefine((value, ctx) => {
    if (value.kind === "edited" && !value.editedText)
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["editedText"], message: "Edição exige o texto humano." });
    if (value.kind === "draft_saved" && !value.draftMessageId)
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["draftMessageId"], message: "Salvamento exige o rascunho." });
    if (value.kind !== "draft_saved" && value.draftMessageId)
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["draftMessageId"], message: "Somente draft_saved pode vincular rascunho." });
    if ((value.kind === "accepted" || value.kind === "discarded") && value.editedText)
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["editedText"], message: "Este feedback não aceita texto editado." });
  });

export type RecordAiFeedbackInput = z.input<typeof recordAiFeedbackSchema>;

export interface RecordAiFeedbackValue {
  feedback: AiFeedback;
}

export function executeRecordAiFeedback(
  stateInput: AiCommandState,
  input: RecordAiFeedbackInput,
  contextInput: CommandContext,
  deps: AiCommandDeps,
): AiExecution<RecordAiFeedbackValue> {
  const context = parseAiCommandContext(contextInput);
  if (!context) return invalidAiCommandContext<RecordAiFeedbackValue>(stateInput);
  const command = "ai.feedback.record";

  let parsed: z.output<typeof recordAiFeedbackSchema>;
  try {
    parsed = recordAiFeedbackSchema.parse(normalizeAiProviderJson(input));
  } catch (error) {
    const message =
      error instanceof z.ZodError ? error.issues[0]?.message ?? "Feedback inválido." : "Feedback inválido.";
    return failure(stateInput, context, "validation_error", message);
  }

  const fingerprint = stableFingerprint({
    ...parsed,
    editedText: parsed.editedText ? contentHash([parsed.editedText]) : undefined,
  });
  const state = cloneState(stateInput);
  const replay = replayLookup<RecordAiFeedbackValue>(state, context, command, fingerprint);
  if (replay) return replay;

  const request = state.aiRequests.find(
    (item) => item.id === parsed.requestId && item.ownerId === context.ownerId,
  );
  if (!request) return failure(stateInput, context, "not_found", "Pedido de IA não encontrado.");

  const result = state.aiResults.find(
    (item) =>
      item.id === parsed.resultId && item.ownerId === context.ownerId && item.requestId === request.id,
  );
  if (!result) return failure(stateInput, context, "not_found", "Resultado de IA não encontrado.");

  if (parsed.draftMessageId) {
    const thread = state.threads.find(
      (item) => item.id === request.threadId && item.ownerId === context.ownerId,
    );
    const draft = state.messages.find(
      (item) =>
        item.id === parsed.draftMessageId &&
        item.ownerId === context.ownerId &&
        item.threadId === request.threadId &&
        item.direction === "outbound" &&
        item.state === "draft" &&
        item.mailboxId === thread?.mailboxId,
    );
    if (!thread || !draft)
      return failure(stateInput, context, "not_found", "Rascunho de saída não encontrado nesta conversa.");
  }

  const now = deps.now();
  const feedback: AiFeedback = {
    id: deps.uuid(),
    ownerId: context.ownerId,
    requestId: request.id,
    resultId: result.id,
    threadId: request.threadId,
    mailboxId: request.mailboxId,
    kind: parsed.kind,
    editedTextHash: parsed.editedText ? contentHash([parsed.editedText]) : undefined,
    draftMessageId: parsed.draftMessageId,
    note: parsed.note,
    commandId: context.commandId,
    correlationId: context.correlationId,
    createdAt: now,
  };

  const membershipId = state.memberships.find(
    (item) => item.leadId === request.leadId && (!request.funnelId || item.funnelId === request.funnelId),
  )?.id;

  // Salvar rascunho é registro operacional; também não conta como interação.
  const events: ActivityEvent[] =
    parsed.kind === "draft_saved"
      ? [
          {
            id: deps.uuid(),
            leadId: request.leadId,
            membershipId,
            type: "ai_suggestion_drafted",
            title: "Rascunho salvo a partir da sugestão de IA",
            occurredAt: now,
            kind: "event",
            actorType: context.actorType,
            actorId: context.actorId,
            origin: context.origin,
            commandId: context.commandId,
            correlationId: context.correlationId,
            countsAsInteraction: false,
          },
        ]
      : [];

  return commit<RecordAiFeedbackValue>({
    state: { ...state, aiFeedback: [...state.aiFeedback, feedback] },
    context,
    command,
    fingerprint,
    targetType: "ai_feedback",
    targetId: feedback.id,
    value: { feedback },
    version: 1,
    events,
    after: { kind: feedback.kind },
    deps,
  });
}

// ---------------------------------------------------------------------------
// Adapter demo
// ---------------------------------------------------------------------------

export interface AiDemoAdapter {
  requestSuggestion(
    input: RequestAiSuggestionInput,
    context: CommandContext,
  ): Promise<CommandResult<RequestAiSuggestionValue>>;
  recordFeedback(
    input: RecordAiFeedbackInput,
    context: CommandContext,
  ): Promise<CommandResult<RecordAiFeedbackValue>>;
  getState(): AiCommandState;
  syncExternal(state: AiCommandState): void;
}

/** Adapter local serializado, no mesmo padrão de tarefas e comunicação. */
export function createAiDemoAdapter(
  initialState: AiCommandState,
  deps: AiCommandDeps,
  onCommit?: (state: AiCommandState) => void,
): AiDemoAdapter {
  let state = cloneState(initialState);
  let queue: Promise<unknown> = Promise.resolve();
  let busy = false;

  const enqueue = <T,>(operation: () => Promise<AiExecution<T>>): Promise<CommandResult<T>> => {
    const run = async () => {
      busy = true;
      try {
        const execution = await operation();
        if (execution.persist) {
          state = execution.state;
          onCommit?.(state);
        }
        return execution.result;
      } finally {
        busy = false;
      }
    };
    if (!busy) {
      const started = run();
      queue = started.then(
        () => undefined,
        () => undefined,
      );
      return started;
    }
    const next = queue.then(run);
    queue = next.then(
      () => undefined,
      () => undefined,
    );
    return next as Promise<CommandResult<T>>;
  };

  return {
    requestSuggestion: (input, context) =>
      enqueue(() => executeRequestAiSuggestion(state, input, context, deps)),
    recordFeedback: (input, context) =>
      enqueue(async () => executeRecordAiFeedback(state, input, context, deps)),
    getState: () => state,
    syncExternal: (nextState) => {
      if (!busy) state = cloneState(nextState);
    },
  };
}
